<template>
  <button
    v-click-outside="validate"
    class="task-button"
    :class="[size, { isEditing, isOneLine }]"
    :tabindex="tabindex"
    @click="click"
  >
    <TaskCheckbox
      status="todo"
      :is-interactive="false"
      :size="checkboxSize"
      @click="click"
    />
    <BaseInput
      ref="inputEl"
      v-model:value="taskTitle"
      class="input smooth"
      placeholder="Add task"
      :is-one-line="isOneLine"
      :textarea-default-rows="1"
      @focus="onFocus"
      @enter="validate"
      @meta-enter="validateAndOpen"
      @alt-enter="validateAndRefocus"
      @escape="clearAndBlur"
    />
  </button>
</template>

<script lang="ts">
import { isEmpty } from "lodash-es";
import { computed, defineComponent, PropType, ref, toRefs } from "vue";

import type {
  ConsoleActionTask,
  GeneralizedTaskId,
  ReferenceId,
  TaskRead,
} from "@ctrl/controller-rpc";

import { CheckboxSize } from "@/components/base/BaseCheckbox.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import * as inject from "@/components/inject";
import TaskCheckbox from "@/components/task/TaskCheckbox.vue";
import { navigateToObject } from "@/utils/navigate";

type Size = "normal" | "withStar" | "compact";

export default defineComponent({
  components: {
    TaskCheckbox,
    BaseInput,
  },
  props: {
    compact: { type: Boolean, default: false },
    /**
     * How the created task is scheduled: on a day (`task-scheduling`), into
     * a week (`task-postponing`), or not at all (`task`). Reuses the
     * console's `ConsoleActionTask` shape, but the task is created directly
     * through the controller — no console involved.
     */
    addTaskAction: {
      type: Object as PropType<ConsoleActionTask>,
      required: true,
    },
    // Replaces the native task creation (e.g. org-scoped lists create a row
    // of the org's bound tasks table instead of a personal task). Resolves
    // with the created task's id (fed to `postValidateAction` like a native
    // creation), or undefined when nothing was created.
    createTask: {
      type: Function as PropType<
        (title: string) => Promise<GeneralizedTaskId | undefined>
      >,
      required: false,
    },
    isOneLine: { type: Boolean, default: false },
    postValidateAction: { type: Function, required: false },
    size: { type: String as PropType<Size>, default: "normal" },
    metricsSource: { type: String },
    tabindex: { type: Number, default: 0 },
  },
  emits: {
    blur: () => true,
    focus: () => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const router = inject.router();
    const {
      addTaskAction,
      createTask,
      postValidateAction,
      size,
      metricsSource,
    } = toRefs(props);
    const isEditing = ref(false);
    const taskTitle = ref("");
    const inputEl = ref<InstanceType<typeof BaseInput>>();
    const checkboxSize = computed<CheckboxSize>(() =>
      size.value === "compact" ? "md" : "lg"
    );
    const focus = () => {
      isEditing.value = true;
      inputEl.value?.focus();
    };
    const click = () => focus();
    const onFocus = () => emit("focus");
    const clearAndBlur = () => {
      taskTitle.value = "";
      isEditing.value = false;
      inputEl.value?.blur();
      emit("blur");
    };
    const validate = async (): Promise<GeneralizedTaskId | null> => {
      if (!isEmpty(taskTitle.value)) {
        const title = taskTitle.value;
        clearAndBlur();
        if (createTask.value) {
          const createdId = (await createTask.value(title)) ?? null;
          if (postValidateAction.value && createdId)
            postValidateAction.value(createdId);
          return createdId;
        }
        const task = ((): TaskRead => {
          switch (addTaskAction.value.type) {
            case "task-scheduling":
              return { title, scheduled: addTaskAction.value.date };
            case "task-postponing":
              return { title, scheduled: addTaskAction.value.batch };
            default:
              return { title };
          }
        })();
        const taskId = await controller.task.create(
          task,
          metricsSource.value
            ? {
                source: "app",
              }
            : {}
        );
        if (postValidateAction.value && taskId)
          postValidateAction.value(taskId);
        return taskId;
      } else {
        clearAndBlur();
        return null;
      }
    };
    const validateAndOpen = async () => {
      const taskId = await validate();
      if (taskId)
        await navigateToObject(router, controller, {
          // A `createTask` override can create a bound task (a database
          // row); navigateToObject routes those to the object screen.
          kind: "task",
          id: taskId,
        } satisfies ReferenceId);
    };
    const validateAndRefocus = async () => {
      const taskId = await validate();
      if (taskId) {
        // Refocus the input to allow creating another task
        isEditing.value = true;
        inputEl.value?.focus();
      }
    };
    return {
      checkboxSize,
      clearAndBlur,
      click,
      focus,
      inputEl,
      isEditing,
      onFocus,
      taskTitle,
      validate,
      validateAndOpen,
      validateAndRefocus,
    };
  },
});
</script>

<style lang="scss" scoped>
.task-button {
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  width: 100%;
  text-align: start;
  border-radius: 3px;
  min-height: 42px;
  padding: 6px 16px;
  column-gap: 12px;
  border: 1px solid transparent;
  &.isOneLine {
    align-items: center;
    height: 42px;
  }
  &:not(.isOneLine) {
    align-items: flex-start;
    height: fit-content;
    padding-top: 10px;
  }
  @include themed() {
    caret-color: t($secondary);
    color: t($primary);
  }
  :deep(.base-input) .input {
    @extend %font-body-regular;
    @extend %font-smooth;
    @include themed() {
      color: t($primary);
    }
  }
  :deep(.textarea) {
    @extend %font-body-regular;
    margin-top: -1px;
  }
  :deep(input[type="text" i]) {
    padding: 0;
  }
  .input {
    width: inherit;
  }
  &.compact {
    min-height: 32px;
    padding-block: 4px;
    padding-inline-start: 8px;
    column-gap: 8px;
    &.isOneLine {
      height: 32px;
    }
    &:not(.isOneLine) {
      padding-top: 6px;
    }
    :deep(.textarea) {
      @extend %font-caption-regular;
      line-height: 17.5px;
      word-break: break-word;
    }
  }
  &:hover {
    cursor: text;
    @include themed() {
      border: 1px solid t($border-inline-element-hover);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }
  &.isEditing {
    @include themed() {
      background: t($bg-inline-element-editing);
      border: 1px solid t($bg-inline-element-editing);
    }
    :deep(.task-checkbox) input[type="checkbox"] {
      transition: none;
      @include themed() {
        background: t($grey-05);
      }
    }
  }
  :deep(.task-checkbox) input[type="checkbox"] {
    transition: none;
  }
}
</style>
